/**
 * Shared page engagement state + context for PostHog events.
 * Answers: where they are, what they see, how long, how far they scroll, what they click.
 */

import { getJourneySnapshot } from "@/lib/posthogVisitorJourney";
import { getWorkerGeoAnalyticsProps } from "@/lib/serverTemplateDetection";

const TEMPLATE_SESSION_KEY = "ph_current_template";

let pageEnterMs = Date.now();
let maxScrollPct = 0;
const sectionsViewed = new Set<string>();
let clickCount = 0;
let templateName: string | null = null;

export function resetPageEngagementState(pathname?: string) {
  pageEnterMs = Date.now();
  maxScrollPct = 0;
  sectionsViewed.clear();
  clickCount = 0;
  if (pathname && typeof sessionStorage !== "undefined") {
    sessionStorage.setItem("ph_last_page_path", pathname);
  }
}

export function setEngagementTemplate(name: string | null) {
  templateName = name;
  if (typeof sessionStorage !== "undefined") {
    if (name) sessionStorage.setItem(TEMPLATE_SESSION_KEY, name);
    else sessionStorage.removeItem(TEMPLATE_SESSION_KEY);
  }
}

export function updateMaxScrollPct(pct: number) {
  if (pct > maxScrollPct) maxScrollPct = pct;
}

export function markSectionViewed(sectionId: string) {
  if (sectionId) sectionsViewed.add(sectionId);
}

export function incrementPageClickCount() {
  clickCount += 1;
}

export function getActiveSecondsOnPage(): number {
  return Math.round((Date.now() - pageEnterMs) / 1000);
}

function referrerPath(): string | null {
  if (!document.referrer) return null;
  try {
    return new URL(document.referrer).pathname;
  } catch {
    return null;
  }
}

/** Context attached to scroll, time, clicks, pulses, and via before_send. */
export function getPageEngagementContext(
  extra?: Record<string, unknown>,
): Record<string, unknown> {
  const geo = getWorkerGeoAnalyticsProps();
  const template =
    templateName ||
    (typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem(TEMPLATE_SESSION_KEY)
      : null);

  const journey = getJourneySnapshot();

  return {
    page_path: window.location.pathname,
    page_url: window.location.href,
    page_title: document.title,
    template,
    journey_intent_level: journey.intent_level,
    funnel_depth: journey.funnel_depth,
    recovery_eligible: journey.recovery_eligible,
    captured_email_domain: journey.captured_email_domain ?? null,
    user_region: localStorage.getItem("user_region") || "LATAM",
    test_country: localStorage.getItem("test_country") || null,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    screen_width: window.screen?.width ?? null,
    screen_height: window.screen?.height ?? null,
    device_pixel_ratio: window.devicePixelRatio ?? 1,
    orientation:
      window.innerWidth > window.innerHeight ? "landscape" : "portrait",
    time_on_page_seconds: getActiveSecondsOnPage(),
    max_scroll_pct: maxScrollPct,
    sections_viewed_count: sectionsViewed.size,
    sections_viewed: Array.from(sectionsViewed).slice(0, 25),
    click_count_page: clickCount,
    referrer_path: referrerPath(),
    visibility_state: document.visibilityState,
    ...Object.fromEntries(
      Object.entries(geo).filter(([, v]) => v !== undefined && v !== null),
    ),
    timestamp: new Date().toISOString(),
    ...extra,
  };
}

function elementLabel(el: Element): string {
  const tagged = el.getAttribute("data-ph-capture") || el.getAttribute("data-attr");
  if (tagged) return tagged.slice(0, 120);
  const aria = el.getAttribute("aria-label");
  if (aria) return aria.slice(0, 120);
  const text = (el.textContent || "").replace(/\s+/g, " ").trim();
  return text.slice(0, 120);
}

function nearestSectionId(el: Element | null): string | null {
  let node: Element | null = el;
  while (node) {
    const section =
      node.getAttribute("data-ph-section") ||
      node.getAttribute("data-section") ||
      (node.tagName === "SECTION" && node.id ? node.id : null);
    if (section) return section;
    node = node.parentElement;
  }
  return null;
}

/** Enrich autocapture and low-level events with click/visibility/geo context. */
export function enrichPostHogEventBeforeSend(
  event: Record<string, unknown> | null,
): Record<string, unknown> | null {
  if (!event || typeof window === "undefined") return event;

  const name = String(event.event ?? "");

  // Session replay: los `$snapshot` (datos rrweb de grabación) pasan INTACTOS.
  // Son de alta frecuencia y pesados; enriquecerlos con contexto de DOM en cada
  // batch es innecesario y arriesga la ingesta de la grabación. Nunca tocarlos.
  if (name === "$snapshot") return event;

  const props = (event.properties as Record<string, unknown>) || {};
  const enriched = {
    ...props,
    ...getPageEngagementContext(),
  };

  if (name === "$autocapture" || name === "$rageclick" || name === "$dead_click") {
    const target = document.activeElement;
    const chain = String(props.$elements_chain ?? "");
    enriched.click_element_chain = chain.slice(0, 500);
    enriched.click_section = nearestSectionId(target);
    if (target) {
      enriched.click_element_tag = target.tagName.toLowerCase();
      enriched.click_element_id = target.id || null;
      enriched.click_element_label = elementLabel(target);
    }
  }

  return { ...event, properties: enriched };
}